import React, { PureComponent } from 'react'
import PropTypes from 'prop-types'
import { withRouter } from 'react-router-dom'

import queryString from 'querystring'

import ApolloClient from 'apollo-client'
import { pathOr } from 'ramda'
import { compose, getContext } from 'recompose'

import { APP_VERSION, VISITED_PAGES } from 'app-constants'
import { ACORNS_HELLO_FLOW } from 'app-constants'
import CenteredSpinner from 'components/CenteredSpinner'
import withPartnerProps, { WithPartnerProps } from 'components/HOCs/WithPartnerProps'
import updateRegistrationMutation from 'components/HOCs/WithResumeFlow/updateRegistration.mutation.gql'
import platform from 'components/Trackers/platform'
import { FlowProviderError, sendToSentry } from 'utils/errors'
import { WithOptimizelyProps, withOptimizely } from 'utils/Optimizely'
import { getTierPrice } from 'utils/TieredRegistration/utils'

import flowMap from './flows'
import {
  FlowEntry,
  FlowProgressWithSteps,
  FlowProviderProps,
  FlowProviderRedirectConfig,
  FlowRoute,
  FlowProviderRedirectConfig as RedirectConfig,
  FlowProviderState as State,
} from './types'
import { getFlow, getFunnelName, getVisitedPages, navStepMap } from './utils'

type Props = FlowProviderProps & WithOptimizelyProps & WithPartnerProps

// This is the provider that provides the redirect logic
class Provider extends PureComponent<Props, State> {
  public static childContextTypes = {
    route: PropTypes.func.isRequired,
    flow: PropTypes.any.isRequired,
    visitedPages: PropTypes.array.isRequired,
    flowProgress: PropTypes.func.isRequired,
    setFlow: PropTypes.func.isRequired,
    setFlowAndUseUpdateRegMutation: PropTypes.func.isRequired,
    isRegistrationFlow: PropTypes.bool.isRequired,
  }

  protected flow: string
  private apolloClient: ApolloClient<any>

  constructor(props) {
    super(props)
    this.apolloClient = props.client
    this.state = { userState: {} }
    this.flow = getFlow()
  }

  public getChildContext() {
    const { route, flow, flowProgress, setFlow, setFlowAndUseUpdateRegMutation } = this

    return {
      route,
      flow,
      visitedPages: getVisitedPages(),
      flowProgress,
      setFlow,
      setFlowAndUseUpdateRegMutation,
      isRegistrationFlow: this.checkIsRegistrationFlow(),
    }
  }

  public setFlow = flow => {
    window.localStorage.setItem(ACORNS_HELLO_FLOW, flow)
    this.flow = getFlow()
  }

  public setFlowAndUseUpdateRegMutation = async flow => {
    this.setFlow(flow)
    return this.updateRegistrationFlow(getFunnelName(flow))
  }

  // Looks for the flow entries, then finds the corresponding entry based on the current page + user action.
  // Then executes the resolver function to know where to go next and redirect
  // It also tracks the step if necessary
  public route = (config: RedirectConfig) => {
    // ToDo: This could be on the browser storage for persistance
    const userState = { ...this.state.userState, ...config.state }
    this.setState({ userState })

    const entries = flowMap[this.flow]
    // If we're unable to specify a `from` (because we're redirecting from a mixin component) we
    // we fall back to the current pathname
    const entry = entries && entries.find(e => e.page === (config.from || location.pathname))
    const action = entry && entry.actions.find(a => a.name === config.action)

    let path = action && action.goTo(userState, { visitedPages: getVisitedPages(), ...this.props })

    path = path || config.path // Use user override if !path

    this.trackStepEvent(action)

    if (path) {
      if (config.queryParams) {
        const stringifiedParams = queryString.stringify(config.queryParams)
        path = `${path}?${stringifiedParams}`
      }

      if (config.forwardQueryParams) {
        path = `${path}${window.location.search}`
      }

      if (this.flowJustStarted(config.from)) {
        this.clearVisitedPages()
      }

      if (!config.doNotTrackVisited) {
        this.addVisitedPage(path)
        config.locationState
          ? (config.locationState['referrer'] = entry.page)
          : (config.locationState = { referrer: entry.page })
      }

      this.props.history.push(path, config.locationState && config.locationState)
    } else {
      const e = new FlowProviderError('Cannot resolve path.')
      sendToSentry(e, {
        extras: {
          flow: this.flow,
          config,
          visitedPages: getVisitedPages(),
          entry,
        },
      })
      throw e
    }
  }

  public flowProgress: (path: string) => FlowProgressWithSteps = path => {
    const entries = flowMap[this.flow]
    const entry = entries && entries.find(e => e.page === path)

    return this.configureFlowProgress(entry)
  }

  public render() {
    if (!this.flow) {
      return <CenteredSpinner />
    } else {
      return <>{this.props.children}</>
    }
  }

  private updateRegistrationFlow = updatedFlow => {
    return this.apolloClient
      .mutate({ mutation: updateRegistrationMutation, variables: { funnel: updatedFlow } })
      .catch(error => {
        sendToSentry(error, {
          message: 'user attempting to resume and fetch a tier subscription for user',
        })
      })
  }

  private checkIsRegistrationFlow() {
    return [
      'later',
      'spend',
      'core',
      'tiered',
      'directToMobile',
      'mightyOakCardOnboarding',
    ].includes(this.flow)
  }

  private trackStepEvent(action) {
    if (action && action.stepEvent && window.analytics) {
      window.analytics.track('Step', {
        app_version: APP_VERSION,
        step_name: action.stepEvent,
        platform,
        funnel: getFunnelName(this.flow),
        partner: pathOr(null, ['org', 'name'], this.props.partner),
        tierPrice: getTierPrice(),
      })
      this.props.trackOptimizely(`step.${action.stepEvent}`)
    }
  }

  private configureFlowProgress: (entry: FlowEntry) => FlowProgressWithSteps = entry => {
    return navStepMap.get(this.flow)(entry, { visitedPages: getVisitedPages(), ...this.props })
  }

  private addVisitedPage = (page: string) => {
    const visitedPages = getVisitedPages()
    visitedPages.push(page)
    window.localStorage.setItem(VISITED_PAGES, JSON.stringify(visitedPages))
  }

  private clearVisitedPages = () => {
    window.localStorage.removeItem(VISITED_PAGES)
  }

  private flowJustStarted = (from: string) => {
    return from === FlowRoute.Welcome || from === FlowRoute.Signup
  }
}

const FlowProvider = compose(withRouter, withOptimizely, withPartnerProps)(Provider)

// This is the HOC for using on the enhanced components.
export const withFlowRouter = compose(
  getContext({
    route: PropTypes.func,
    flow: PropTypes.string,
    visitedPages: PropTypes.array,
    flowProgress: PropTypes.func,
    setFlow: PropTypes.func,
    setFlowAndUseUpdateRegMutation: PropTypes.func,
    isRegistrationFlow: PropTypes.bool,
  }),
)

export type WithFlowRouterProps = {
  route: (config: FlowProviderRedirectConfig) => void
  flow: string
  visitedPages: string[]
  flowProgress: (path: string) => FlowProgressWithSteps
  setFlow: (flow: string) => void
  setFlowAndUseUpdateRegMutation: (flow: string) => Promise<any>
  isRegistrationFlow: boolean
}

export default FlowProvider
